import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { forwardRef } from "react";
import { DraggableAttributes } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useTimelineItem } from "./image-list-item-hooks";
import ImageOverlayControls from "./ImageOverlayControls";
import ImageFormControls from "./ImageFormControls";
import Image from "@/lib/components/Image";
import {
  IconExclamationCircle,
  IconGripVertical,
  IconLoader2,
} from "@tabler/icons-react";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import "./image-list-item.css";
import ImageTransitionDialog from "../TransitionDialog/ImageTransitionDialog";
import CompositionTransitionDialog from "../TransitionDialog/CompositionTransitionDialog";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { isSourceImageProcessing } from "@repo/api/utils/composition";

type Props = {
  index?: number;
  timelineItem: CreativeProjectTimelineItem;
  style?: React.CSSProperties;
  isDraggingSelf?: boolean;
  isDraggingAny?: boolean;
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
  isDragOverlay?: boolean;
  totalTimelineItems?: number;
};

export const ImageListItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      index,
      timelineItem,
      isDraggingSelf,
      listeners,
      attributes,
      style,
      isDraggingAny,
      totalTimelineItems,
    },
    ref,
  ) => {
    const {
      status,
      sourceImage,
      animatedImage,
      timelineItemFormData,
      selectTimelineItemForAnimation,
      removeCompositionImage,
      setAnimatedImage,
    } = useTimelineItem(timelineItem);
    const seekToImage = useEditorStore((state) => state.service.seekToImage);
    const sourceImages = useEditorStore((state) => state.sourceImages);

    const opacity = isDraggingSelf ? "opacity-0" : "";
    const isFirstItem = index === 0;
    const isLastItem =
      totalTimelineItems !== undefined && index === totalTimelineItems - 1;

    const startImage = sourceImages[timelineItem.startSourceImageExternalId];
    const endImage = timelineItem.endSourceImageExternalId
      ? sourceImages[timelineItem.endSourceImageExternalId]
      : undefined;
    const isGenerating =
      (startImage && isSourceImageProcessing(startImage)) ||
      (endImage && isSourceImageProcessing(endImage));
    const isFailed =
      startImage?.status === "FAILED" || endImage?.status === "FAILED";

    return (
      <div
        className={`image-transition-root flex ${opacity}`}
        ref={ref}
        style={style}
      >
        <div
          {...attributes}
          {...ignorePortaledEvents(listeners)}
          className="group relative flex shrink-0 items-center"
          onClick={() => seekToImage(timelineItem.externalId)}
        >
          <div className="flex h-full w-8 min-w-8 cursor-grab items-center justify-center">
            <IconGripVertical
              size={16}
              className="text-text-secondary duration-200 group-hover:text-text-primary"
            />
          </div>
          <div className="image-transition-image relative z-0 h-40.75 w-62.5 shrink-0">
            <ImageOverlayControls
              status={status}
              timelineItem={timelineItem}
              selectTimelineItemForAnimation={selectTimelineItemForAnimation}
              removeCompositionImage={removeCompositionImage}
              setAnimatedImage={setAnimatedImage}
            />
            <div className="h-full w-full overflow-hidden rounded-xl bg-white">
              <Image
                src={sourceImage!.thumbnailSrc}
                className="h-full w-full object-cover object-center"
              />
            </div>
            {isGenerating && <ImageGeneratingOverlay />}
            {isFailed && !isGenerating && <ImageFailedOverlay />}
          </div>

          {!isDraggingAny && isFirstItem && (
            <CompositionTransitionDialog type="START" />
          )}
          {!isDraggingAny && !isFirstItem && (
            <ImageTransitionDialog timelineItem={timelineItem} />
          )}
          {!isDraggingAny && isLastItem && (
            <CompositionTransitionDialog type="END" />
          )}
        </div>
        <div className="flex-1 overflow-hidden">
          <ImageFormControls
            timelineItem={timelineItem}
            animatedImage={animatedImage}
            timelineItemFormData={timelineItemFormData}
            status={status}
          />
        </div>
      </div>
    );
  },
);

type SortableImageListItemProps = {
  index: number;
  timelineItem: CreativeProjectTimelineItem;
  totalTimelineItems: number;
};

export function SortableImageListItem({
  index,
  timelineItem,
  totalTimelineItems,
}: SortableImageListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, active } =
    useSortable({ id: timelineItem.externalId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ImageListItem
      ref={setNodeRef}
      index={index}
      style={style}
      timelineItem={timelineItem}
      isDraggingSelf={active?.id === timelineItem.externalId}
      isDraggingAny={!!active?.id}
      attributes={attributes}
      listeners={listeners}
      totalTimelineItems={totalTimelineItems}
    />
  );
}

// The transition dialogs portal out of the item, but their React events still
// bubble through it and would otherwise start a drag.
function ignorePortaledEvents(listeners?: SyntheticListenerMap) {
  if (!listeners) return undefined;
  return Object.fromEntries(
    Object.entries(listeners).map(([name, listener]) => [
      name,
      (event: React.SyntheticEvent) => {
        if (event.currentTarget.contains(event.target as Node)) {
          listener(event);
        }
      },
    ]),
  );
}

function ImageGeneratingOverlay() {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-black/50">
      <IconLoader2 size={18} className="animate-spin text-white opacity-90" />
      <p className="mt-2 text-xs font-semibold text-white opacity-90">
        Generating image
      </p>
    </div>
  );
}

function ImageFailedOverlay() {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-black/50">
      <IconExclamationCircle size={18} className="text-white opacity-90" />
      <p className="mt-2 text-xs font-semibold text-white opacity-90">
        Image generation failed
      </p>
    </div>
  );
}
